import React, { useMemo } from 'react';
import { formatAddressForDisplay, addressStyles } from '../utils/addressFormatters';

const ComparableProperties = React.memo(({ propertyData, listing }) => {
  const simulatedComps = useMemo(() => {
    if (!propertyData?.data?.estimatedValue || !listing?.address) return [];
    
    const baseValue = propertyData.data.estimatedValue;
    const baseAddress = listing.address.split(' ');
    const streetName = baseAddress.slice(1).join(' ');
    const baseHouseNumber = parseInt(baseAddress[0]) || 100;
    
    return Array(3).fill(null).map((_, i) => ({
      address: `${baseHouseNumber + ((i + 1) * 2)} ${streetName}`,
      bedrooms: 3 + i,
      bathrooms: (2 + (i * 0.5)).toFixed(1),
      livingArea: (1200 + (i * 300)),
      lastSold: (() => {
        const date = new Date();
        date.setMonth(date.getMonth() - ((i + 1) * 3));
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      })(),
      price: Math.round(baseValue * [0.85, 1.0, 1.15][i])
    }));
  }, [propertyData?.data?.estimatedValue, listing?.address]);

  const hasRealComps = propertyData?.data?.comparableProperties?.length > 0;
  const comps = hasRealComps ? propertyData.data.comparableProperties : simulatedComps;

  if (!comps || comps.length === 0) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 p-4">
        <div className="text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-sm">No comparable properties available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 divide-y divide-green-100">
      {comps.map((comp, i) => (
        <div key={i} className="p-4 flex justify-between items-center hover:bg-green-100 transition-colors">
          <div className="flex-1">
            <div className={`${addressStyles.address} font-semibold text-gray-900`}>
              {formatAddressForDisplay(comp.address)}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {[
                comp.bedrooms && `${comp.bedrooms} bed`,
                comp.bathrooms && `${comp.bathrooms} bath`,
                comp.livingArea && `${comp.livingArea.toLocaleString()} sqft`
              ].filter(Boolean).join(', ')}
            </div>
            {comp.lastSold && (
              <div className="text-xs text-green-600 font-medium mt-1">
                Last sold: {comp.lastSold}
              </div>
            )}
          </div>
          <div className="text-lg font-bold text-gray-900 ml-4">
            ${typeof comp.price === 'number' ? comp.price.toLocaleString() : 
               (typeof comp.price === 'string' ? comp.price.replace(/[^0-9]/g, '') : '0').toLocaleString()}
          </div>
        </div>
      ))}
      
      {/* Show data source indicator */}
      <div className="p-3 text-xs text-center text-gray-600 bg-green-50 font-medium">
        {hasRealComps ? (
          <span className="flex items-center justify-center">
            <svg className="w-4 h-4 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
            </svg>
            Real comparable data from Zillow
          </span>
        ) : (
          <span className="flex items-center justify-center">
            <svg className="w-4 h-4 mr-1 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Simulated comparable properties
          </span>
        )}
      </div>
    </div>
  );
});

ComparableProperties.displayName = 'ComparableProperties';

export default ComparableProperties;